import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  AdminReviewApiError,
  clearStoredAdminReviewToken,
  connectAdminReviewSession,
  getAdminReviewPage,
  getAdminReviewPages,
  getStoredAdminReviewToken,
  type AdminReviewPageDetail,
  type AdminReviewPageSummary,
  type AdminReviewSiteKey,
} from './api/adminReviewApi';
import './pages-review.css';

type PagesReviewProps = {
  siteKey: AdminReviewSiteKey;
};

function getErrorMessage(error: unknown) {
  if (error instanceof AdminReviewApiError) {
    return error.message;
  }

  return 'The staging Page review request failed unexpectedly.';
}

function formatDate(value: string | null) {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export default function PagesReview({ siteKey }: PagesReviewProps) {
  const [sessionToken, setSessionToken] = useState(() => getStoredAdminReviewToken());
  const [accessKey, setAccessKey] = useState('');
  const [pages, setPages] = useState<AdminReviewPageSummary[]>([]);
  const [selectedPage, setSelectedPage] = useState<AdminReviewPageDetail | null>(null);
  const [siteName, setSiteName] = useState('');
  const [siteDomain, setSiteDomain] = useState('');
  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setPages([]);
    setSelectedPage(null);
    setSiteName('');
    setSiteDomain('');
    setMessage('');

    if (!sessionToken) return undefined;

    const controller = new AbortController();
    setLoadingPages(true);

    void getAdminReviewPages(siteKey, sessionToken, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setPages(response.pages);
        setSiteName(response.site.name);
        setSiteDomain(response.site.primaryDomain);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;

        if (error instanceof AdminReviewApiError && error.code === 'unauthorized') {
          clearStoredAdminReviewToken();
          setSessionToken('');
        }

        setMessage(getErrorMessage(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingPages(false);
      });

    return () => controller.abort();
  }, [sessionToken, siteKey]);

  const formattedBodyJson = useMemo(() => {
    if (!selectedPage) return '';

    try {
      return JSON.stringify(JSON.parse(selectedPage.page.bodyJson), null, 2);
    } catch {
      return selectedPage.page.bodyJson;
    }
  }, [selectedPage]);

  const connect = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setConnecting(true);
    setMessage('');

    try {
      const token = await connectAdminReviewSession(accessKey);
      setAccessKey('');
      setSessionToken(token);
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = () => {
    clearStoredAdminReviewToken();
    setSessionToken('');
    setAccessKey('');
    setPages([]);
    setSelectedPage(null);
    setSiteName('');
    setSiteDomain('');
    setMessage('');
  };

  const openPage = async (page: AdminReviewPageSummary) => {
    if (!sessionToken) return;

    setLoadingDetail(true);
    setMessage('');

    try {
      const detail = await getAdminReviewPage(siteKey, page.id, sessionToken);
      setSelectedPage(detail);
    } catch (error) {
      if (error instanceof AdminReviewApiError && error.code === 'unauthorized') {
        disconnect();
      }
      setMessage(getErrorMessage(error));
    } finally {
      setLoadingDetail(false);
    }
  };

  if (!sessionToken) {
    return (
      <section className="review-access-card" aria-labelledby="review-access-title">
        <div className="review-access-copy">
          <span className="eyebrow">Protected staging access</span>
          <h2 id="review-access-title">Review imported CMS Pages</h2>
          <p>
            Enter the temporary staging review key to inspect draft Page records. The key is kept only in this browser tab session and is never embedded in the Admin build.
          </p>
        </div>
        <form className="review-access-form" onSubmit={connect}>
          <label htmlFor="review-access-key">Staging review access key</label>
          <input
            id="review-access-key"
            type="password"
            value={accessKey}
            onChange={(event) => setAccessKey(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder="Enter access key"
          />
          <button className="primary-action" type="submit" disabled={connecting || !accessKey.trim()}>
            {connecting ? 'Connecting…' : 'Open read-only review'}
          </button>
          {message && <p className="review-message error" role="alert">{message}</p>}
        </form>
      </section>
    );
  }

  return (
    <div className="pages-review-shell">
      <section className="review-toolbar">
        <div>
          <div className="review-badges">
            <span className="review-badge">Read-only</span>
            <span className="review-badge secondary">Staging</span>
          </div>
          <h2>{siteName || 'CMS Page review'}</h2>
          <p>
            {siteDomain
              ? `${siteDomain} · ${pages.length} Page record${pages.length === 1 ? '' : 's'}`
              : 'Loading site-scoped Page records…'}
          </p>
        </div>
        <button className="secondary-action" type="button" onClick={disconnect}>
          Disconnect review session
        </button>
      </section>

      {message && <p className="review-message error" role="alert">{message}</p>}

      <div className="pages-review-grid">
        <section className="review-list-panel" aria-label="Imported Pages">
          <div className="review-panel-heading">
            <div>
              <span className="eyebrow">Imported content</span>
              <h3>Pages</h3>
            </div>
            <span>{loadingPages ? 'Loading…' : pages.length}</span>
          </div>

          {loadingPages && <p className="review-empty">Loading protected staging Page records…</p>}

          {!loadingPages && pages.length === 0 && !message && (
            <p className="review-empty">No Page records exist for this site in staging.</p>
          )}

          <div className="review-page-list">
            {pages.map((page) => (
              <button
                className={selectedPage?.page.id === page.id ? 'review-page-row active' : 'review-page-row'}
                type="button"
                key={page.id}
                onClick={() => void openPage(page)}
              >
                <span className="review-page-main">
                  <strong>{page.title}</strong>
                  <small>/{page.slug || ''}</small>
                </span>
                <span className={`status status-${page.status.toLowerCase()}`}>{page.status}</span>
                <span className="review-page-arrow" aria-hidden="true">→</span>
              </button>
            ))}
          </div>
        </section>

        <section className="review-detail-panel" aria-label="Page details">
          {!selectedPage && !loadingDetail && (
            <div className="review-detail-placeholder">
              <span className="eyebrow">Page inspector</span>
              <h3>Select a Page</h3>
              <p>Choose an imported Page to inspect its transformed CMS document, migration provenance and SEO state.</p>
            </div>
          )}

          {loadingDetail && <p className="review-empty">Loading Page details…</p>}

          {selectedPage && !loadingDetail && (
            <div className="review-detail-content">
              <div className="review-detail-heading">
                <div>
                  <span className="eyebrow">Page inspector</span>
                  <h3>{selectedPage.page.title}</h3>
                  <p>/{selectedPage.page.slug}</p>
                </div>
                <span className={`status status-${selectedPage.page.status.toLowerCase()}`}>
                  {selectedPage.page.status}
                </span>
              </div>

              <dl className="review-metadata-grid">
                <div><dt>Template</dt><dd>{selectedPage.page.templateKey}</dd></div>
                <div><dt>Updated</dt><dd>{formatDate(selectedPage.page.updatedAt)}</dd></div>
                <div><dt>Legacy source ID</dt><dd>{selectedPage.page.legacySourceId || '—'}</dd></div>
                <div><dt>Robots</dt><dd>{selectedPage.page.seo.robotsIndex ? 'index' : 'noindex'}, {selectedPage.page.seo.robotsFollow ? 'follow' : 'nofollow'}</dd></div>
              </dl>

              <div className="review-detail-section">
                <h4>Summary</h4>
                <p>{selectedPage.page.summary || 'No summary was imported.'}</p>
              </div>

              <div className="review-detail-section">
                <h4>Migration provenance</h4>
                <dl className="review-stacked-metadata">
                  <div><dt>Legacy URL</dt><dd>{selectedPage.page.legacySourceUrl || '—'}</dd></div>
                  <div><dt>Created in platform</dt><dd>{formatDate(selectedPage.page.createdAt)}</dd></div>
                </dl>
              </div>

              <div className="review-detail-section">
                <h4>SEO</h4>
                <dl className="review-stacked-metadata">
                  <div><dt>Title</dt><dd>{selectedPage.page.seo.title || '—'}</dd></div>
                  <div><dt>Description</dt><dd>{selectedPage.page.seo.description || '—'}</dd></div>
                  <div><dt>Canonical URL</dt><dd>{selectedPage.page.seo.canonicalUrl || '—'}</dd></div>
                </dl>
              </div>

              <div className="review-detail-section">
                <div className="review-code-heading">
                  <h4>Transformed BodyJson</h4>
                  <span>CMS contract v1</span>
                </div>
                <pre className="review-json">{formattedBodyJson}</pre>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
